import { CompactTable } from "@table-library/react-table-library/compact";
import { Column, TableNode } from "@table-library/react-table-library/types";
import cx from "classnames";
import React, { useState } from "react";
import { toast } from "react-toastify";

import { humanizeError } from "@/lib/humanizeError";
import { useDataList } from "@/lib/useData";
import { useEffectOnChangeOnly } from "@/lib/useStableEffect";
import { useStateQuery } from "@/lib/useStateQuery";
import PartLoader from "@/PartLoader";
import PartTablePagination from "@/PartTablePagination";

import styles from "./PartTable.module.scss";

export type TableDataItemBase = { id: string | number };

export type TableColumn<T> = Column<TableNode> & {
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortKey?: Extract<keyof T, string> | string;
};

export type TableColumnsT<T> = TableColumn<T>[];

export type BuildDataUrlT<T> = ({
  page,
  size,
  sortKey,
  sortReverse,
}: {
  page: number;
  size: number;
  sortKey?: Extract<keyof T, string> | string;
  sortReverse?: boolean;
}) => string | undefined;

interface Table2Props<T extends TableDataItemBase> {
  tableName: string;
  buildDataUrl: BuildDataUrlT<T>;
  columns: TableColumnsT<T>;
  className?: string;
  rowsPerPage?: number;
  showLoaderOnlyOnce?: boolean;
  noHeaders?: boolean;
  noOutsideMargins?: boolean;
}

export interface Table2Ref {
  refresh: () => void;
}

function Table2Inner<T extends TableDataItemBase>(
  props: Table2Props<T>,
  ref: React.Ref<Table2Ref>,
) {
  const {
    tableName,
    buildDataUrl,
    columns,
    className,
    rowsPerPage = 50,
    showLoaderOnlyOnce,
    noHeaders,
    noOutsideMargins,
  } = props;

  const [page = 1, setPage, buildUrl] = useStateQuery<number>(tableName + "Page", {
    valueToQuery: page => (page !== undefined && page >= 2 ? String(page) : undefined),
    queryToValue: value => (Number(value) >= 2 ? Number(value) : 1),
  });

  const size = rowsPerPage;
  const safePage = page;

  const dummyData: T[] = Array.from({ length: 10 }).map(
    (_, i) => ({ id: i, _dummy: true }) as unknown as T,
  );

  const [data, setData] = useState<{ id: string; nodes: T[] }>({ id: "0", nodes: [] });

  const [sort, setSort] = useStateQuery<
    | {
        sortKey: Extract<keyof T, string> | string;
        sortReverse: boolean;
      }
    | undefined
  >(tableName + "Sort", {
    valueToQuery: value => {
      if (value?.sortKey && value?.sortReverse) return "DESC:" + value.sortKey;
      if (value?.sortKey) return "ASC:" + value.sortKey;
    },
    queryToValue: value => {
      if (value?.startsWith("ASC:"))
        return {
          sortKey: value.replace(/^ASC:/, "") as keyof T & string,
          sortReverse: false,
        };
      if (value?.startsWith("DESC:"))
        return {
          sortKey: value.replace(/^DESC:/, "") as keyof T & string,
          sortReverse: true,
        };
    },
  });

  const [initialyLoaded, setInitialyLoaded] = React.useState<boolean>(false);

  const dataUrl = React.useMemo(() => {
    return buildDataUrl?.({
      page: safePage,
      size,
      sortKey: sort?.sortKey,
      sortReverse: sort?.sortReverse,
    });
  }, [safePage, size, sort?.sortKey, sort?.sortReverse, buildDataUrl]);

  const [data1, dataLoading, { meta: { total: unsafeTotal } = {}, error, refresh }] =
    useDataList<T>(dataUrl || "", { noCache: true });

  React.useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  React.useEffect(() => {
    if (error) toast.error(humanizeError(error));
  }, [error]);

  React.useEffect(() => {
    if (data1 && !dataLoading && !initialyLoaded) setInitialyLoaded(true);
  }, [data1, dataLoading, initialyLoaded]);

  const safeTotal: number = (unsafeTotal as number) || 0;

  useEffectOnChangeOnly(() => {
    setPage(1);
  }, [buildDataUrl]);

  // On empty list drop to page 1, good for initial render and good after delete something
  React.useEffect(() => {
    if (data1?.length === 0) setPage(1);
  }, [data1]);

  /* Remove unknown sort from a query */
  React.useEffect(() => {
    if (
      sort?.sortKey &&
      !columns
        .map(it => it.sortKey)
        .filter(Boolean)
        .includes(sort?.sortKey)
    ) {
      setSort(undefined);
    }
  }, [columns, sort]);

  React.useEffect(() => {
    setData({ id: "0", nodes: data1 || dummyData });
  }, [data1]);

  const gridTemplate = React.useMemo(() => {
    return columns
      .map(column => {
        if (column.width) return column.width + "px";
        let str = "";
        str += "minmax(";
        str += column.minWidth ?? 0;
        str += "px, ";
        if (column.maxWidth) str += column.maxWidth + "px";
        else str += "1fr";
        str += ")";
        return str;
      })
      .join(" ");
  }, [JSON.stringify(columns)]);

  const sortableColumns = React.useMemo(() => {
    return columns.map(column => {
      const isSorted = sort?.sortKey && sort?.sortKey === column.sortKey;

      return {
        ...column,
        label: (
          <button
            className={styles.sortLabel}
            onClick={() => {
              const sortKey = column.sortKey;
              if (!sortKey) return;
              if (sort?.sortKey === sortKey && sort?.sortReverse)
                return setSort(undefined);
              if (sort?.sortKey === sortKey)
                return setSort({ sortKey, sortReverse: true });
              return setSort({ sortKey, sortReverse: false });
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: column.sortKey ? "pointer" : undefined,
              userSelect: "none",
            }}
          >
            {column.label}
            {isSorted && (
              <span style={{ marginLeft: 4 }}>{sort?.sortReverse ? "▼" : "▲"}</span>
            )}
          </button>
        ),
      };
    });
  }, [columns, sort]);

  let showLoader = false;
  if (showLoaderOnlyOnce) showLoader = !initialyLoaded && dataLoading;
  else if (dataLoading) showLoader = true;

  return (
    <div
      className={cx(styles.table, className, {
        [styles.noHeaders]: !!noHeaders,
        [styles.noOutsideMargins]: !!noOutsideMargins,
      })}
      style={{
        // @ts-expect-error: CSS custom property
        "--data-table-library_grid-template-columns1": gridTemplate,
      }}
    >
      <PartLoader block active={showLoader}>
        <CompactTable
          columns={sortableColumns}
          data={data}
          layout={{
            custom: true,
          }}
        />
      </PartLoader>
      <PartTablePagination
        className={styles.paginationContainer}
        total={safeTotal}
        currentPage={safePage}
        pageSize={size}
        buildUrl={buildUrl}
      />
    </div>
  );
}

const Table2 = React.forwardRef(Table2Inner) as <T extends TableDataItemBase>(
  props: Table2Props<T> & { ref?: React.Ref<Table2Ref> },
) => ReturnType<typeof Table2Inner>;

export default Table2;
